import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { getRandomId } from '../../utils/get-random-id';
import { InputEvent, SelectionChangeEvent } from './combobox.types';
import { isKeyboardClickOnFocusedElement } from '../../utils/is-keyboard-click-on-focused-element';
import { GreyBoxSize } from '../grey-box/grey-box.types';

/**
 * @slot - Used to place the list items shown in the overlay (required)
 * @slot input - Used to place the wb-combobox-input (required)
 * @slot message - Used to place a wb-control-hint or wb-control-error below the input (optional)
 */
@Component({
  tag: 'wb-combobox',
  styleUrl: './combobox.scss',
  shadow: true,
})
export class ComboboxComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** The size of the input */
  @Prop({ reflect: true })
  public size?: GreyBoxSize = 'l';

  @State()
  public hasMessage: boolean = false;

  @Listen('keydown')
  public handleKeyDown(event: KeyboardEvent): void {
    if (isKeyboardClickOnFocusedElement(this.inputEl, event)) {
      if (!this.overlayEl.open) {
        this.openOverlay();
      } else {
        this.closeOverlay();
      }
    }
  }

  private overlayEl: HTMLWbComboboxListElement;
  private inputEl: HTMLWbComboboxInputElement;
  private inputClicked = false;

  private TagNames = transformTagNames(this, 'wb-combobox', ['wb-combobox-input', 'wb-combobox-list', 'wb-control-error', 'wb-control-hint']);

  public componentWillLoad(): void {
    this.setupInputElement();
  }

  private prefillInputWithSelectedItems() {
    if (this.overlayEl) {
      this.overlayEl.getSelectedComboboxListItemEls().then((items) => {
        if (items) {
          this.inputEl.selectedValue = items;
        }
      });
    }
  }

  private setupInputElement() {
    this.inputEl = this.el.querySelector<HTMLWbComboboxInputElement>(`${this.TagNames.WbComboboxInput}`);

    this.inputEl.size = this.size;

    this.inputEl.addEventListener('wbcomboboxinputclick', this.handleInputClicked);
    this.inputEl.addEventListener('wbcomboboxinputchange', this.handleInputChange);
    this.inputEl.addEventListener('wbcomboboxinput', this.handleInputChange);
  }

  @Watch('size')
  public handleSizeChange(): void {
    this.inputEl.size = this.size;
  }

  private setupListElement() {
    this.overlayEl = this.el.querySelector<HTMLWbComboboxListElement>(`${this.TagNames.WbComboboxList}`);

    if (this.overlayEl) {
      this.overlayEl.addEventListener('wbcomboboxlistclose', this.closeOverlay);
      this.overlayEl.addEventListener('wbcomboboxlistselectionchange', this.onSelectionChanged);
    }
  }

  private handleInputChange = (event: CustomEvent<InputEvent>) => {
    this.openOverlay();
    this.overlayEl.searchValue = event.detail.value;
  };

  private handleInputClicked = () => {
    if (!this.overlayEl.open) {
      this.openOverlay();
    } else {
      this.closeOverlay();
    }
  };

  private openOverlay = () => {
    const shouldOpenOverlay = !this.overlayEl?.open && !this.inputEl?.readonly && !this.inputEl?.disabled;
    if (shouldOpenOverlay) {
      this.overlayEl.open = true;
      this.inputEl.open = true;
      this.inputEl.setAttribute('aria-expanded', 'true');
    }
  };

  private closeOverlay = (event?: CustomEvent<{ clearInput: boolean }>) => {
    this.overlayEl.open = false;
    this.inputEl.open = false;
    this.inputEl.setAttribute('aria-expanded', 'false');

    this.inputClicked = false;

    if (event?.detail.clearInput) {
      this.inputEl.value = '';
    }
  };

  private onSelectionChanged = (event: CustomEvent<SelectionChangeEvent>) => {
    this.inputEl.selectedValue = event.detail.value;
    this.overlayEl.searchValue = '';
  };

  private handleMessageSlotChange = () => {
    this.handleMessageContent();
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      messageEl.id = messageEl.id || `message-${getRandomId()}`;
      this.inputEl.setAttribute('aria-describedby', messageEl.id);
    } else {
      this.inputEl.removeAttribute('aria-describedby');
    }

    this.inputEl.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  private handleMessageContent = () => {
    const messageSlot: HTMLSlotElement = this.el.shadowRoot.querySelector('slot[name="message"]');
    this.hasMessage = messageSlot && messageSlot.assignedNodes().length > 0;
  };

  private handleOverlaySlotChange = () => {
    this.setupListElement();
    this.prefillInputWithSelectedItems();

    // If input already focused, should open overlay
    if (this.inputClicked) {
      this.openOverlay();
    }
  };

  public render(): JSX.Element {
    return (
      <Host class={{ 'wb-combobox': true, 'has-message': this.hasMessage }} data-on={getDataOnFromTheme(this.el, this.theme)}>
        <slot name="input" />
        <div class="wrapper">
          <slot name="message" onSlotchange={this.handleMessageSlotChange} />
          <slot onSlotchange={this.handleOverlaySlotChange} />
        </div>
      </Host>
    );
  }
}
